import React, { useEffect, useRef, useState } from 'react';
import api from '../api/api';
import { contextError } from '../utils/operatingContext';

type Resource = { id: number; name: string };
type Administration = {
  enabled: boolean; version: number; canAdministerPolicy: boolean;
  users: { id: string; userName: string }[];
  warehouses: Resource[]; kitchens: Resource[]; venues: Resource[];
};
type Grant = {
  id: number; warehouseId: number | null; kitchenId: number | null; venueId: number | null;
  effectiveFrom: string; effectiveTo: string | null; cancelled: boolean; closedAtUtc: string | null;
};
type Account = { userId: string; version: number; unrestricted: boolean; allowLegacyUnscoped: boolean; grants: Grant[] };
export default function OperatingAccessSettings({ onChanged }: { onChanged: () => void }) {
  const [administration, setAdministration] = useState<Administration | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [userId, setUserId] = useState('');
  const [resource, setResource] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [modeNeedsReload, setModeNeedsReload] = useState(false);
  const sequence = useRef(0);
  useEffect(() => {
    let active = true;
    const requests = sequence;
    Promise.all([
      api.get<Administration>('/operating-contexts/administration'),
      api.get<{ permissions: string[]; editPermissions: string[] }>('/users/me/access'),
    ]).then(([settings, access]) => {
      if (active) {
        setAdministration(settings.data);
        setCanEdit(access.data.permissions.includes('page.admin') && access.data.editPermissions.includes('page.admin'));
      }
    }).catch((failure: unknown) => { if (active) setError(contextError(failure)); });
    return () => { active = false; requests.current += 1; };
  }, []);
  const loadUser = async (id: string) => {
    const current = ++sequence.current;
    setUserId(id); setAccount(null); setError(''); setMessage('');
    if (!id) return;
    setBusy(true);
    try {
      const response = await api.get<Account>(`/operating-contexts/users/${encodeURIComponent(id)}`);
      if (current === sequence.current) setAccount(response.data);
    } catch (failure: unknown) { if (current === sequence.current) setError(contextError(failure)); }
    finally { if (current === sequence.current) setBusy(false); }
  };
  const save = async (addGrant: boolean, closeId?: number) => {
    if (!account || !canEdit) return;
    const current = ++sequence.current;
    const [resourceType, rawId] = resource.split(':');
    setBusy(true); setError(''); setMessage('');
    let committed = false;
    try {
      await api.put(`/operating-contexts/users/${encodeURIComponent(account.userId)}`, {
        version: account.version, unrestricted: account.unrestricted, allowLegacyUnscoped: account.allowLegacyUnscoped,
        addGrants: addGrant ? [{ resourceType, resourceId: Number(rawId), effectiveFrom: from, effectiveTo: to || null }] : [],
        closeGrants: closeId ? [{ id: closeId, effectiveTo: from }] : [],
      });
      committed = true;
      setAccount(null);
      setMessage('Доступ збережено. Історичні документи не змінювалися.');
      onChanged();
      const response = await api.get<Account>(`/operating-contexts/users/${encodeURIComponent(account.userId)}`);
      if (current === sequence.current) {
        setAccount(response.data);
      }
    } catch (failure: unknown) {
      if (current === sequence.current) {
        setError(`${committed ? 'Зміни вже збережено, але повторне завантаження не вдалося. ' : ''}${contextError(failure)} Завантажте користувача повторно перед збереженням.`);
        setAccount(null);
      }
    } finally { if (current === sequence.current) setBusy(false); }
  };
  const reloadAdministration = async () => {
    setBusy(true); setError('');
    try {
      const response = await api.get<Administration>('/operating-contexts/administration');
      setAdministration(response.data); setModeNeedsReload(false);
    } catch (failure: unknown) { setError(contextError(failure)); }
    finally { setBusy(false); }
  };
  const toggleMode = async () => {
    if (!administration || !canEdit || modeNeedsReload || !window.confirm(
      administration.enabled ? 'Вимкнути контекстні обмеження для всіх користувачів?'
        : 'Увімкнути обмеження? Користувачі без явних призначень втратять контекстний доступ. Історичні документи не зміняться.')) return;
    setBusy(true); setError('');
    try {
      const { data } = await api.put<{ enabled: boolean; version: number }>('/operating-contexts/mode', {
        version: administration.version, enabled: !administration.enabled,
      });
      setAdministration({ ...administration, ...data }); onChanged();
    } catch (failure: unknown) { setError(contextError(failure)); setModeNeedsReload(true); }
    finally { setBusy(false); }
  };
  const resourceName = (grant: Grant) => {
    if (grant.warehouseId) return `Склад: ${administration?.warehouses.find(item => item.id === grant.warehouseId)?.name || grant.warehouseId}`;
    if (grant.kitchenId) return `Кухня: ${administration?.kitchens.find(item => item.id === grant.kitchenId)?.name || grant.kitchenId}`;
    return `Зал: ${administration?.venues.find(item => item.id === grant.venueId)?.name || grant.venueId}`;
  };
  return <section aria-label="Адміністрування контекстного доступу">
    <h2>Явні призначення доступу</h2>
    <p>Потрібні глобальні права адміністрування та окремий серверний список адміністраторів доступу. Минулі періоди не переписуються. Дата завершення не входить у період доступу.</p>
    {error && <p role="alert">{error}</p>}{message && <p role="status">{message}</p>}
    <button type="button" disabled={busy} onClick={() => void reloadAdministration()}>Оновити налаштування доступу</button>
    {administration && <>
      {administration.canAdministerPolicy && <button type="button" disabled={!canEdit || busy || modeNeedsReload} onClick={() => void toggleMode()}>
        {administration.enabled ? 'Вимкнути контекстні обмеження' : 'Увімкнути контекстні обмеження'}
      </button>}
      <label>Користувач<select disabled={busy} value={userId} onChange={event => void loadUser(event.target.value)}>
        <option value="">Оберіть користувача</option>
        {administration.users.map(user => <option key={user.id} value={user.id}>{user.userName}</option>)}
      </select></label>
      {userId && <button type="button" disabled={busy} onClick={() => void loadUser(userId)}>Оновити доступ користувача</button>}
      {account && <>
        <label><input type="checkbox" disabled={!canEdit || busy || !administration.canAdministerPolicy} checked={account.unrestricted}
          onChange={event => setAccount({ ...account, unrestricted: event.target.checked })} />Явний необмежений контекстний доступ</label>
        <label><input type="checkbox" disabled={!canEdit || busy || !administration.canAdministerPolicy} checked={account.allowLegacyUnscoped}
          onChange={event => setAccount({ ...account, allowLegacyUnscoped: event.target.checked })} />Історичні записи без явного ресурсу</label>
        <button type="button" disabled={!canEdit || busy} onClick={() => void save(false)}>Зберегти спеціальні дозволи</button>
        <div className="operating-contexts__actions">
          <label>Ресурс доступу<select value={resource} disabled={busy || !canEdit} onChange={event => setResource(event.target.value)}>
            <option value="">Оберіть ресурс</option>
            {(['warehouses', 'kitchens', 'venues'] as const).flatMap(type => administration[type].map(item =>
              <option key={`${type}:${item.id}`} value={`${({ warehouses: 'warehouse', kitchens: 'kitchen', venues: 'venue' } as const)[type]}:${item.id}`}>
                {{ warehouses: 'Склад', kitchens: 'Кухня', venues: 'Зал' }[type]}: {item.name}
              </option>))}
          </select></label>
          <label>Початок / дата відкликання<input type="date" value={from} disabled={busy || !canEdit} onChange={event => setFrom(event.target.value)} /></label>
          <label>До (не включно, необов’язково)<input type="date" value={to} disabled={busy || !canEdit} onChange={event => setTo(event.target.value)} /></label>
          <button type="button" disabled={busy || !canEdit || !resource || !from} onClick={() => void save(true)}>Додати доступ</button>
        </div>
        <ul>{account.grants.map(grant => <li key={grant.id}>
          {resourceName(grant)}: [{grant.effectiveFrom}, {grant.effectiveTo || 'без кінця'}) {grant.cancelled ? 'скасовано до початку' : ''}
          {!grant.cancelled && <button type="button" disabled={busy || !canEdit || !from} onClick={() => void save(false, grant.id)}>Відкликати з обраної дати</button>}
        </li>)}</ul>
      </>}
    </>}
  </section>;
}
